import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBanner, Spinner } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { statusColor } from '@platform/core/lib/statusColors';
import { useAllRecords } from '@platform/engine/insights/useAllRecords';
import { useListParams } from '@platform/engine/list/useListParams';
import { AnalyticsBars } from './AnalyticsBars';
import { AnalyticsDue } from './AnalyticsDue';
import { analyticsFieldsOf } from './analyticsFields';
import { AnalyticsKpis } from './AnalyticsKpis';
import { analyticsOf } from './analyticsOf';
import { AnalyticsTrend } from './AnalyticsTrend';
import type { IAnalyticsBodyProps } from './IAnalyticsBodyProps';
import './analytics.css';

/**
 * Загальна аналітика модуля (прототип v4 isDashboard) за записами з фільтрами сторінки (model.query — useListParams: пошук, швидкий фільтр, теги,
 * розширені фільтри; patterns/filtering): тайли показників, «За статусом» (кольори статусів),
 * «Відкриті за виконавцем» (шість найбільших; тип без виконавця — один рядок «Не призначено»), «Створено й завершено · 8 тижнів», «Термін
 * протягом 7 днів» (тип без терміну — «Цього тижня термінів немає.»), «Відкриті за пріоритетом» (лише з полем пріоритету) — як у прототипі.
 */
export function AnalyticsDashboard({ model }: IAnalyticsBodyProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { ws, rt, lang } = model;
  const { records, isLoading, error } = useAllRecords(ws, rt.key, useListParams(model));
  const fields = useMemo(() => analyticsFieldsOf(rt), [rt]);
  const data = useMemo(() => analyticsOf(records, rt, fields, Date.now()), [records, rt, fields]);
  if (error) return <ErrorBanner error={error} />;
  if (isLoading && !records.length) return <Spinner />;
  const remainingField = rt.fields.find((f) => f.key === fields.remaining);
  const fieldLabel = remainingField ? strings.field(rt.key, remainingField.key, remainingField.label) : '';
  const remainingLabel = !remainingField ? undefined
    : remainingField.key === 'remaining' ? t('modBody.dash.remaining') : t('modBody.dash.openSum', { field: fieldLabel.replace(/\s*\(.*\)$/, '') });
  const statusBars = data.byStatus.map((b) => ({
    key: b.key, count: b.count, color: statusColor(rt.statuses, b.key, rt.key), label: strings.status(rt.key, b.key, rt.statuses.list.find((s) => s.value === b.key)?.label),
  }));
  const people = data.byAssignee.map((b) => ({ key: b.key || '-', count: b.count, color: 'var(--an-purple)', label: b.label ?? t('modBody.dash.unassigned') }));
  const priorityField = rt.fields.find((f) => f.key === fields.priority);
  const priorities = (data.byPriority ?? []).map((b) => ({
    key: b.key, count: b.count, color: 'var(--an-orange)', label: strings.option(rt.key, priorityField?.key ?? '', b.key, priorityField?.options?.find((o) => o.value === b.key)?.label),
  }));
  return (
    <div className="an">
      <AnalyticsKpis data={data} remainingLabel={remainingLabel} />
      <div className="an-grid">
        <AnalyticsBars title={t('modBody.dash.byStatus')} bars={statusBars} />
        <AnalyticsBars title={t('modBody.dash.byAssignee')} bars={people} />
        <AnalyticsTrend weeks={data.weeks} />
        <AnalyticsDue records={data.due} dueKey={fields.due} lang={lang} onOpen={model.openRecord} />
        {data.byPriority && <AnalyticsBars title={t('modBody.dash.byPriority')} bars={priorities} />}
      </div>
    </div>
  );
}
